import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

const ShowcaseSection = styled.section`
  padding: 80px 0;
  background: linear-gradient(
    135deg,
    var(--background-light) 0%,
    var(--background-medium) 100%
  );
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;

  h2 {
    font-family: "Inter", sans-serif;
    font-size: 3.2rem;
    color: var(--text-dark);
    margin-bottom: 1rem;
  }

  p {
    font-family: "Inter", sans-serif;
    font-size: 1.2rem;
    color: var(--text-light);
    max-width: 600px;
    margin: 0 auto;
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
`;

const ProductCard = styled.div`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(4, 120, 87, 0.12);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 50px rgba(4, 120, 87, 0.18);
  }
`;

const ProductImage = styled.div`
  height: 250px;
  background: linear-gradient(
    135deg,
    var(--primary-color) 0%,
    var(--primary-light) 100%
  );
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const ProductInfo = styled.div`
  padding: 25px;

  h3 {
    font-family: "Inter", sans-serif;
    font-size: 1.6rem;
    color: var(--text-dark);
    margin-bottom: 10px;
  }

  p {
    font-family: "Inter", sans-serif;
    color: var(--text-light);
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 15px;
  }

  .price {
    font-family: "Inter", sans-serif;
    font-size: 1.3rem;
    font-weight: 700;
    color: var(--secondary-color);
    margin-bottom: 15px;
  }
`;

const ViewButton = styled.button`
  width: 100%;
  padding: 12px;
  background: var(--secondary-color);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;

  &:hover {
    background: #c9a660;
    transform: translateY(-2px);
  }
`;

const ProductShowcase = () => {
  const navigate = useNavigate();

  const handleViewDetails = () => {
    navigate("/containers");
  };

  const products = [
    {
      id: 1,
      name: "20ft New Container",
      description:
        "Brand new, one-trip container perfect for storage and modifications",
      price: "Starting at $2,000",
      image:
        "/assets/2025/08/20ft-shipping-container-with-all-doors-open-600x398.jpg",
    },
    {
      id: 2,
      name: "40ft High Cube",
      description: "Extra height container ideal for oversized storage needs",
      price: "Starting at $3,200",
      image: "/assets/2025/08/40ft-high-cube-shipping-container-2.jpg",
    },
    {
      id: 3,
      name: "Mobile Office Container",
      description: "Fully equipped mobile office solution ready for deployment",
      price: "Starting at $8,500",
      image: "/assets/2025/08/10-x-40-luxury-style-tiny-home.jpg",
    },
    {
      id: 4,
      name: "10ft Storage Container",
      description: "Compact storage solution perfect for residential use",
      price: "Starting at $1,500",
      image: "/assets/2025/08/10ft-shipping-container-600x450.jpg",
    },
    {
      id: 5,
      name: "Refrigerated Container",
      description: "Temperature-controlled storage for specialized needs",
      price: "Starting at $4,000",
      image: "/assets/2025/08/20ft-bar-container-600x450.jpg",
    },
    {
      id: 6,
      name: "Custom Workshop",
      description:
        "Converted container workshop with tools and electrical setup",
      price: "Starting at $18,000",
      image: "/assets/2025/08/20ft-offshore-shipping-container.jpg",
    },
  ];

  return (
    <ShowcaseSection>
      <Container>
        <SectionHeader>
          <h2>Our Container Products</h2>
          <p>
            High-quality containers designed for strength, security, and
            versatility. From storage to custom builds, we have the perfect
            solution.
          </p>
        </SectionHeader>

        <ProductGrid>
          {products.map((product) => (
            <ProductCard key={product.id}>
              <ProductImage>
                <img src={product.image} alt={product.name} />
              </ProductImage>
              <ProductInfo>
                <h3>{product.name}</h3>
                <p>{product.description}</p>
                <div className="price">{product.price}</div>
                <ViewButton onClick={handleViewDetails}>
                  View Details
                </ViewButton>
              </ProductInfo>
            </ProductCard>
          ))}
        </ProductGrid>
      </Container>
    </ShowcaseSection>
  );
};

export default ProductShowcase;
